import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
} from 'react-native';
import Svg, { Path, G, ClipPath } from 'react-native-svg';

import {
  Factory,
  MapPin,
  Server,
  Zap,
  Battery,
  Snowflake,
  Droplet,
  Laptop,
  Eye,
  FileText,
  Star,
  Building2,
  ClipboardList,
  ChevronRight,
  Package,
  Shield,
} from './icons';
import { REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH } from '../constants/requestFormLayout';
import { useRequestScope } from '../contexts/RequestScopeContext';
import { useEmbeddedSidebarFooterNavPadding } from '../hooks/useEmbeddedSidebarFooterNavPadding';
import { useStepVisibility } from '../hooks/useStepVisibility';
import { partitionSidebarNavSteps } from '../utils/requestFormNavGroups';
import { shouldShowStepInSidebarNav } from '../utils/sidebarScopeStepVisibility';

interface SidebarPreviewProps {
  activeItem?: string;
  onItemPress?: (item: string) => void;
  navigate?: (path: string) => void;
  onStepChange?: (stepId: string) => void;
  showSummaryStep?: boolean;
  onExpand?: () => void;
  // Permission-based step filtering
  requestStage?: string;
  requestId?: string | null;
  usePermissionFiltering?: boolean;
  // IT Admin role for audit item visibility
  isITAdministrator?: boolean;
  topOffset?: number;
  embeddedInFrame?: boolean;
}

// Vertiv Logo Icon SVG Component
const VertivLogoIcon: React.FC<{ width?: number; height?: number }> = ({
  width = 32,
  height = 32,
}) => (
  <Svg viewBox="0 0 50 48" width={width} height={height}>
    <G clipPath="url(#cp1)">
      <ClipPath id="cp1">
        <Path d="m0 0.28h190v47h-190z" />
      </ClipPath>
      <Path
        d="m74.7 28.9l-5.3-14.9h-5.8l8.4 22.2h5.3l8.4-22.2h-5.6z"
        fill="#000"
      />
      <Path
        d="m89.4 36.2h16.6v-4.7h-11.4v-4.3h9.4v-4.5h-9.4v-4h11.4v-4.7h-16.6z"
        fill="#000"
      />
      <Path
        d="m24.9 23.6l9.7-9.6h12.6c-4.1-8.1-12.5-13.7-22.3-13.7-9.7 0-18.1 5.5-22.2 13.7h12.5l9.7 9.6z"
        fill="#000"
      />
      <Path
        d="m24.9 36.1l11.2 11.2c8.2-4.1 13.8-12.5 13.8-22.2 0-3.9-0.9-7.7-2.7-11.1z"
        fill="#000"
      />
      <Path
        d="m0 25.1c0 9.7 5.6 18.1 13.7 22.2l11.2-11.2-22.2-22.1c-1.8 3.4-2.7 7.2-2.7 11.1z"
        fill="#000"
      />
      <Path
        d="m129.3 21.5c0-4.2-3.4-7.5-7.5-7.5h-10.4v22.3h5.2v-7.2h2.4l4.9 7.2h6.1l-5.6-7.8c2.8-1 4.9-3.8 4.9-7zm-5.5 0.3c0 1.6-1.4 2.9-3 2.9h-4.3v-6h4.3c1.7 0 3 1.4 3 3q0 0.1 0 0.1z"
        fill="#000"
      />
      <Path d="m132.3 18.7h6.8v17.5h5.2v-17.5h6.8v-4.7h-18.8z" fill="#000" />
      <Path
        d="m189.4 32.8l-1.1 2.6-1-2.6h-0.7v3.4h0.6v-2.4l0.9 2.4h0.4l0.9-2.4v2.4h0.6v-3.4z"
        fill="#000"
      />
      <Path
        d="m175.3 28.9l-5.3-14.9h-5.8l8.4 22.2h5.3l8.4-22.2h-5.6z"
        fill="#000"
      />
      <Path d="m183.5 33.3h1.1v2.9h0.6v-2.9h1.1v-0.5h-2.8z" fill="#000" />
      <Path d="m160.6 14h-5.2v22.2h5.2z" fill="#000" />
    </G>
  </Svg>
);

const ALL_SIDEBAR_FORM_STEPS = [
  {
    id: 'opportunity',
    icon: Factory,
    label: 'Opportunity',
    route: '/create-request',
    step: 1,
  },
  {
    id: 'site-information',
    icon: Building2,
    label: 'Site Information',
    route: '/create-request',
    step: 2,
  },
  {
    id: 'facility-power',
    icon: Zap,
    label: 'Facility Power Readiness',
    route: '/facility-power',
    step: 4,
  },
  {
    id: 'large-ups',
    icon: Battery,
    label: 'Large UPS',
    route: '/large-ups',
    step: 4.5,
  },
  {
    id: 'power-distribution',
    icon: Zap,
    label: 'Power Distribution',
    route: '/power-distribution',
    step: 4.6,
  },
  {
    id: 'rack-power',
    icon: Server,
    label: 'Racks',
    route: '/rack-power',
    step: 5,
  },
  {
    id: 'small-ups',
    icon: Battery,
    label: 'Small UPS',
    route: '/small-ups',
    step: 7,
  },
  {
    id: 'cooling-readiness',
    icon: Snowflake,
    label: 'Cooling Readiness',
    route: '/cooling-readiness',
    step: 8,
  },
  {
    id: 'liquid-cooling',
    icon: Droplet,
    label: 'Liquid Cooling',
    route: '/liquid-cooling',
    step: 9,
  },
  {
    id: 'customer-it',
    icon: Laptop,
    label: 'Customer IT Equipment',
    route: '/customer-it',
    step: 10,
  },
  {
    id: 'monitoring',
    icon: Eye,
    label: 'Monitoring Management',
    route: '/monitoring',
    step: 11,
  },
  {
    id: 'documents',
    icon: FileText,
    label: 'Supporting Documents',
    route: '/documents',
    step: 12,
  },
  {
    id: 'feedback',
    icon: Star,
    label: 'Feedback',
    route: '/feedback',
    step: 13,
  },
  {
    id: 'summary',
    icon: ClipboardList,
    label: 'Summary',
    route: '/summary',
    step: 14,
  },
] as const;

type SidebarPreviewFormStep = (typeof ALL_SIDEBAR_FORM_STEPS)[number] & {
  step: number;
};

function buildSidebarPreviewFormSteps(): SidebarPreviewFormStep[] {
  const list = ALL_SIDEBAR_FORM_STEPS.filter(s => {
    if (s.id === 'customer-it') return false;
    if (s.id === 'feedback') return false;
    if (s.id === 'monitoring') return false;
    return true;
  });
  return list.map((s, i) => {
    const step = { ...s, step: i + 1 };
    if (step.id === 'cooling-readiness') {
      return { ...step, label: 'Cooling' as const };
    }
    return step;
  }) as SidebarPreviewFormStep[];
}

export const SidebarPreview: React.FC<SidebarPreviewProps> = ({
  activeItem = 'opportunity',
  onItemPress,
  navigate,
  onStepChange,
  showSummaryStep = true,
  onExpand,
  requestStage,
  requestId,
  usePermissionFiltering = true,
  isITAdministrator = false,
  topOffset = 0,
  embeddedInFrame = false,
}) => {
  // Get step visibility based on permissions
  // Pass requestId to ensure proper cache invalidation when switching between requests
  const { stepVisibility, isLoading: isLoadingPermissions } = useStepVisibility(
    usePermissionFiltering ? requestStage : undefined,
    usePermissionFiltering ? requestId || undefined : undefined,
  );

  const requestScope = useRequestScope();

  const formSteps = React.useMemo(() => {
    const base = buildSidebarPreviewFormSteps();
    if (!requestScope || !onStepChange) {
      return base;
    }
    const enabled = new Set(requestScope.enabledStepIds);
    return base.filter(step => enabled.has(step.id));
  }, [requestScope, onStepChange]);

  // Filter steps based on showSummaryStep prop and permissions
  let visibleSteps = showSummaryStep
    ? formSteps
    : formSteps.filter(step => step.id !== 'summary');

  // Apply permission filtering if enabled (#997: user-added optional scopes bypass RBAC nav hide)
  if (usePermissionFiltering && !isLoadingPermissions) {
    visibleSteps = visibleSteps.filter(step =>
      shouldShowStepInSidebarNav(step.id, {
        stepVisibility,
        enabledStepIds: requestScope?.enabledStepIds ?? [],
        storedStepIds: requestScope?.storedStepIds,
      }),
    );
  }

  const { primarySteps, footerSteps } = React.useMemo(
    () => partitionSidebarNavSteps(visibleSteps),
    [visibleSteps],
  );

  const footerNavPaddingBottom =
    useEmbeddedSidebarFooterNavPadding(embeddedInFrame);
  const footerNavStyle = React.useMemo(
    () =>
      footerNavPaddingBottom != null
        ? { paddingBottom: footerNavPaddingBottom }
        : undefined,
    [footerNavPaddingBottom],
  );

  const renderPreviewStep = (step: SidebarPreviewFormStep) => (
    <TouchableOpacity
      key={step.id}
      testID={`wizard-section-${step.id}`}
      style={[
        styles.sidebarItem,
        activeItem === step.id && styles.sidebarItemActive,
      ]}
      onPress={() => {
        if (onStepChange) {
          onStepChange(step.id);
        } else if (navigate) {
          navigate(step.route);
        }
        onItemPress?.(step.id);
      }}
    >
      <View style={styles.sidebarIconContainer}>
        <step.icon
          size={16}
          color={activeItem === step.id ? '#e55a2b' : '#666'}
        />
      </View>
    </TouchableOpacity>
  );

  // Mirrors the expanded Sidebar exactly: same gate, same position inside the pinned footer
  // group immediately above Summary. See the comment there for why the item is hardcoded and
  // why the gate does not read the stage.
  const deliverablesNavItem =
    onStepChange && requestId ? (
      <TouchableOpacity
        key="deliverables"
        testID="sidebar-preview-deliverables-item"
        style={[
          styles.sidebarItem,
          activeItem === 'deliverables' && styles.sidebarItemActive,
        ]}
        onPress={() => {
          onStepChange('deliverables');
          onItemPress?.('deliverables');
        }}
      >
        <View style={styles.sidebarIconContainer}>
          <Package
            size={16}
            color={activeItem === 'deliverables' ? '#e55a2b' : '#666'}
          />
        </View>
      </TouchableOpacity>
    ) : null;

  const footerNavNodes: React.ReactNode[] = [];
  footerSteps.forEach(step => {
    if (step.id === 'summary' && deliverablesNavItem) {
      footerNavNodes.push(deliverablesNavItem);
    }
    footerNavNodes.push(renderPreviewStep(step));
  });
  if (deliverablesNavItem && !footerSteps.some(step => step.id === 'summary')) {
    footerNavNodes.push(deliverablesNavItem);
  }

  return (
    <View
      testID="sidebar-preview"
      style={[
        styles.sidebarPreview,
        !embeddedInFrame && { top: topOffset },
        embeddedInFrame && styles.sidebarPreviewEmbedded,
        embeddedInFrame && {
          width: REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH,
          minWidth: REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH,
          maxWidth: REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH,
          flexBasis: REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH as any,
        },
      ]}
    >
      {/* Header Section - expand button only; global header owns the logo. */}
      <View style={styles.sidebarHeader}>
        {/* Expand button */}
        <TouchableOpacity
          style={styles.expandButton}
          onPress={onExpand}
          testID="sidebar-preview-expand"
          accessibilityRole="button"
          accessibilityLabel="Expand request steps"
        >
          <ChevronRight size={16} color="#666" />
        </TouchableOpacity>
      </View>

      {/* Navigation: primary steps scroll; footer steps pinned above sidebar bottom */}
      <View style={styles.sidebarNavBody}>
        <ScrollView
          style={styles.sidebarPrimaryScroll}
          contentContainerStyle={styles.sidebarPrimaryContent}
          showsVerticalScrollIndicator={true}
          testID="sidebar-preview-scroll"
        >
          {primarySteps.map(renderPreviewStep)}
        </ScrollView>
        {footerNavNodes.length > 0 ? (
          <View style={[styles.navFooterPinned, footerNavStyle]}>
            <View
              style={styles.navFooterDivider}
              testID="sidebar-preview-nav-footer-divider"
            />
            {footerNavNodes}
          </View>
        ) : null}
        {/* Impersonation Audit icon - IT Admin only, RequestDetail only */}
        {isITAdministrator &&
          onStepChange &&
          requestId &&
          requestId !== 'new-request' && (
            <View style={[styles.auditFooterSlot, footerNavStyle]}>
              <View style={styles.auditDivider} />
              <TouchableOpacity
                style={[
                  styles.sidebarItem,
                  activeItem === 'impersonation-audit' &&
                    styles.sidebarItemActive,
                ]}
                onPress={() => {
                  if (onStepChange) {
                    onStepChange('impersonation-audit');
                  }
                  onItemPress?.('impersonation-audit');
                }}
              >
                <View style={styles.sidebarIconContainer}>
                  <Shield
                    size={16}
                    color={
                      activeItem === 'impersonation-audit' ? '#e55a2b' : '#666'
                    }
                  />
                </View>
              </TouchableOpacity>
            </View>
          )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sidebarPreview: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: REQUEST_FORM_SIDEBAR_COLLAPSED_WIDTH,
    backgroundColor: '#fff', // White background
    zIndex: 1001, // Higher than overlay to stay on top
    flexDirection: 'column',
    ...Platform.select({
      web: {
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)',
        display: 'flex' as const,
        minHeight: 0,
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {
          width: 2,
          height: 0,
        },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 5,
      },
    }),
  },
  sidebarPreviewEmbedded: {
    position: 'relative' as any,
    top: undefined,
    bottom: undefined,
    flexGrow: 0,
    flexShrink: 0,
    height: '100%' as any,
    flexDirection: 'column',
    ...Platform.select({
      web: {
        display: 'flex' as const,
        minHeight: 0,
      },
    }),
  },
  sidebarHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: 14,
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 6,
  },
  expandButton: {
    width: 32,
    height: 32,
    padding: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e1e5ea',
    borderRadius: 6,
  },
  sidebarNavBody: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    flexDirection: 'column',
    ...Platform.select({
      web: {
        display: 'flex' as const,
        overflow: 'hidden' as const,
      },
    }),
  },
  sidebarPrimaryScroll: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    ...Platform.select({
      web: {
        overflowY: 'auto' as const,
      },
    }),
  },
  sidebarPrimaryContent: {
    paddingHorizontal: 8,
    paddingBottom: 8,
    width: '100%',
  },
  navFooterPinned: {
    flexShrink: 0,
    paddingHorizontal: 8,
    paddingTop: 8,
    paddingBottom: 16,
    width: '100%',
  },
  navFooterDivider: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginBottom: 8,
    marginHorizontal: 4,
  },
  auditFooterSlot: {
    flexShrink: 0,
    paddingHorizontal: 8,
    paddingBottom: 16,
  },
  sidebarItem: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    marginVertical: 2,
    borderRadius: 6,
    minHeight: 40,
  },
  sidebarItemActive: {
    backgroundColor: 'rgba(229, 90, 43, 0.2)', // Semi-transparent orange background
  },
  sidebarIconContainer: {
    width: 20,
    alignItems: 'center',
  },
  auditDivider: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    marginBottom: 8,
  },
});
